<template>
  <div>
    <b-modal-custom
      title="Vessels"
      :modal-class.sync="$options.data.themeClass"
      :visible="value"
      @change="$emit('input', $event)"
      size="xl"
      :ok-only="true"
      ok-title="Close"
    >
      <div>
        <div class="table-container">
          <div class="search">
            <b-button
              @click="handleExport"
              :disabled="loading"
              variant="primary"
              size="sm"
              class="download-button"
            >
              CSV
            </b-button>

            <b-input-group size="sm">
              <b-form-input
                v-model="filterString"
                type="search"
                placeholder="Type to Search"
              >
              </b-form-input>
              <b-input-group-append>
                <b-button @click="filterString = ''">Clear</b-button>
              </b-input-group-append>
            </b-input-group>
          </div>

          <vue-perfect-scrollbar><!-- instead of "responsive" for b-table -->
            <b-table
              :dark="darkMode"
              :hover="true"
              :striped="true"
              :bordered="false"
              :sorting="true"
              :small="true"
              :filter="filterString"
              @filtered="handleFiltered"
              :per-page="perPage"
              :current-page="currentPage"
              :items="vessels"
              :fields="fields">
              <template v-if="loading" #top-row>
                <b-td :colspan="fields.length">
                  <div class="text-center">
                    <i class="fa fa-2x fa-cog fa-spin text-muted m-2"></i>
                  </div>
                </b-td>
              </template>
            </b-table>
          </vue-perfect-scrollbar>

          <nav>
            <b-pagination
              :total-rows="totalRows"
              :per-page="perPage"
              
              v-model="currentPage"
              class="m-0"
              prev-text="Prev"
              next-text="Next"
              hide-goto-end-buttons
            />
          </nav>
        </div>
      </div>
    </b-modal-custom>

    <export-modal
      v-model="csvModal.show"
      file-type="csv"
      :file-link="csvModal.link"
      :alert-text="csvModal.alertText"
    />
  </div>
</template>

<script>
import getNested from "lodash-es/get";
import { mapActions, mapGetters } from "vuex";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import ExportModal from "../widgets/ExportModal";
import { regions } from "@/shared/constants/regions";
import cTable from '../Table.vue'
import moment from "moment";
import BModalCustom from '@/base_components/BModalCustom'



const ladenStatusTable = {
  L: "Laden",
  B: "Ballast"
};
const portCallStatusTable = {
  not_in_port: "At Sea",
  in_berth: "In Berth",
  in_anchorage: "In Anchorage",
  in_port: "In Port",
  in_shipyard: "In Shipyard"
};
const ladenStatusBackwardTable = {
  // it seems api still requires L/B as a "ladenStatus"
  Laden: "L",
  Ballast: "B"
};

export default {
  name: "vessels-modal",

  components: {
    BModalCustom,
    ExportModal,
    VuePerfectScrollbar,
    cTable,
  },

  props: {
    value: {
      type: Boolean,
      default: false
    },

    filterValues: {
      type: Object,
      default: null
    }
  },

  data() {
    let fields = [
      {
        key: "imo",
        label: "IMO"
      },
      {
        key: "vesselName",
        label: "Vessel name"
      },
      {
        key: "segment",
        label: "Segment"
      },
      {
        key: "dwt",
        label: "DWT"
      },
      {
        key: "ladenStatus",
        label: "Laden status"
      },
      {
        key: "zoneName",
        label: "Zone name"
      },
      {
        key: "commodityGroup",
        label: "Commodity"
      },
      {
        key: "portCallStatus",
        label: "Status"
      },
      {
        key: "relatedPortName",
        label: "Current Port"
      },
      {
        key: "cargoStatus",
        cargo: "Voyage Status"
      },
      {
        key: "destinationRegion",
        label: "Dest"
      },
      {
        key: "destination",
        label: "Dest (raw)"
      },
      {
        key: "eta",
        label: "Eta (reported)"
      }
    ];

    return {
      loading: true,
      perPage: 20,
      currentPage: 1,
      filterString: "",
      totalRows: 0,
      csvModal: {
        show: false,
        link: null,
        alertText: null
      },

      fields: fields,
      vessels: []
    };
  },

  async mounted() {
    await this.loadVessels();
  },

  methods: {
    ...mapActions("api/livestates", ["getVessels"]),

    async loadVessels() {
      if (!this.value) {
        this.vessels = [];
        return;
      }

      this.loading = true;
      const apiFilterParameters = {
        ...this.filterValues,
        ladenStatus: this.filterValues.ladenStatus.map(
          statusName => ladenStatusBackwardTable[statusName]
        )
      };

      let response = await this.getVessels(apiFilterParameters);
      this.vessels = getNested(response, "vessels", []).map(item => {
        const region =
          regions.find(region => region.regionId === item.destinationRegion) ||
          {};

        return {
          ...item,
          portCallStatus: portCallStatusTable[item.portCallStatus] || item.portCallStatus,
          ladenStatus: ladenStatusTable[item.ladenStatus] || "--",
          dwt: Math.round(item.dwt/1000) +"k",
          relatedPortName: item.relatedPortName || "--",
          destinationRegion:
            item.destinationPortName ||
            region.regionName ||
            region.regionId ||
            "--",
          eta: moment(item.eta).format("MMM Do YYYY, HH:mm")
        };
      });
      this.totalRows = this.vessels.length;
      this.loading = false;
    },

    async handleExport() {
      let result = await this.getVessels({
        ...this.filterValues,
        format: "csv"
      });
      this.csvModal.link = result.csv;
      this.csvModal.alertText = null;
      this.csvModal.show = true;
    },

    handleFiltered(filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1;
    }
  },

  computed: {
    ...mapGetters("gui", ["darkMode"])
  },

  watch: {
    async value() {
      await this.loadVessels();
    }
  }
};
</script>

<style scoped lang="scss">
.search {
  margin-bottom: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .download-button,
  .download-button:focus,
  .download-button:active {
    margin-right: 15px;
    padding: 1px 6px;
    outline: none !important;
    box-shadow: none !important;
  }
}
</style>
